import { RefreshCw, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useState } from 'react';
import { Disclosure } from '@/components/disclosure';
import { StatusBadge, type StatusTone } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import { Item, ItemContent } from '@/components/ui/item';
import { toastApiError } from '@/lib/toast';
import { type Confidence, type Fact, type Insight, type Verdict, useAiFeedback } from '../api/ai';

const CONFIDENCE: Record<Confidence, { label: string; tone: StatusTone }> = {
  ALTA: { label: 'Confiança alta', tone: 'success' },
  MEDIA: { label: 'Confiança média', tone: 'warning' },
  BAIXA: { label: 'Confiança baixa', tone: 'neutral' },
};

/** Fatos que sustentam a resposta — valores do troig, não da IA (§181, "dados utilizados"). */
export function EvidenceList({ facts, ids }: { facts: Fact[]; ids: string[] }) {
  const used = facts.filter((fact) => ids.includes(fact.id));
  if (used.length === 0) return null;
  return (
    <Item variant="muted" size="sm">
      <ItemContent>
        <p className="text-xs font-medium text-muted-foreground">Baseado em</p>
        <dl className="grid gap-1 sm:grid-cols-label sm:gap-x-4">
          {used.map((fact) => (
            <div key={fact.id} className="contents">
              <dt className="text-muted-foreground">{fact.label}</dt>
              <dd className="tabular">{fact.value}</dd>
            </div>
          ))}
        </dl>
      </ItemContent>
    </Item>
  );
}

/**
 * Rodapé comum às respostas de IA: de onde veio, confiança, limitações, todos os dados enviados,
 * avaliação e "gerar de novo". Deixa claro que é opinião da IA sobre números do troig.
 */
export function InsightFooter({
  insight,
  confidence,
  limitations,
  onRedo,
  redoing,
}: {
  insight: Pick<Insight<unknown>, 'id' | 'facts' | 'model' | 'createdAt' | 'cached' | 'myFeedback'>;
  /** Ausente quando a função não declara confiança (ex.: recomendação do agente de anúncios). */
  confidence?: Confidence;
  limitations: string[];
  onRedo: () => void;
  redoing: boolean;
}) {
  const feedback = useAiFeedback();
  const [verdict, setVerdict] = useState<Verdict | null>(insight.myFeedback);

  function rate(next: Verdict) {
    setVerdict(next);
    feedback
      .mutateAsync({ insightId: insight.id, verdict: next })
      .catch((error: unknown) => toastApiError(error));
  }

  return (
    <div className="space-y-3 border-t pt-3">
      {limitations.length > 0 && (
        <div className="text-xs text-muted-foreground">
          <p className="font-medium">Limitações</p>
          <ul className="list-disc pl-4">
            {limitations.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}
      <Disclosure size="xs" summary={`Todos os dados enviados à IA (${insight.facts.length})`}>
        <dl className="grid gap-1 sm:grid-cols-label sm:gap-x-4">
          {insight.facts.map((fact) => (
            <div key={fact.id} className="contents">
              <dt className="text-muted-foreground">{fact.label}</dt>
              <dd className="tabular">{fact.value}</dd>
            </div>
          ))}
        </dl>
      </Disclosure>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {confidence && (
            <StatusBadge tone={CONFIDENCE[confidence].tone}>
              {CONFIDENCE[confidence].label}
            </StatusBadge>
          )}
          <span>
            Gerado por IA ({insight.model}) em {new Date(insight.createdAt).toLocaleString('pt-BR')}
            {insight.cached ? ' · resposta guardada' : ''}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant={verdict === 'USEFUL' ? 'default' : 'ghost'}
            size="icon"
            aria-label="Útil"
            aria-pressed={verdict === 'USEFUL'}
            onClick={() => rate('USEFUL')}
          >
            <ThumbsUp aria-hidden />
          </Button>
          <Button
            variant={verdict === 'NOT_USEFUL' ? 'default' : 'ghost'}
            size="icon"
            aria-label="Não útil"
            aria-pressed={verdict === 'NOT_USEFUL'}
            onClick={() => rate('NOT_USEFUL')}
          >
            <ThumbsDown aria-hidden />
          </Button>
          <Button variant="ghost" size="sm" disabled={redoing} onClick={onRedo}>
            <RefreshCw aria-hidden className={redoing ? 'animate-spin' : undefined} /> Gerar de novo
          </Button>
        </div>
      </div>
    </div>
  );
}
